import type { IStoragePort } from "@src/domain/ports";
import type { ISidebarStatusRepository } from "@src/domain/repositories/sidebar";

const SIDEBAR_STORAGE_KEY = "sidebarCollapsed";

/**
 * Persists sidebar collapsed state via storage port - localStorage.
 * Infrastructure layer - implements domain interface.
 */
export class SidebarStatusRepository implements ISidebarStatusRepository {
  private readonly storage: IStoragePort;
  private readonly storagePrefix: string;
  private cachedCollapsed: boolean | null = null;

  constructor(storage: IStoragePort, storagePrefix: string) {
    this.storage = storage;
    this.storagePrefix = storagePrefix;
  }

  getCollapsedSync(): boolean {
    try {
      const raw =
        typeof localStorage === "undefined"
          ? null
          : localStorage.getItem(`${this.storagePrefix}:${SIDEBAR_STORAGE_KEY}`);
      if (!raw) return false;
      const item = JSON.parse(raw) as { value?: boolean };
      return item?.value === true;
    } catch {
      return false;
    }
  }

  async resetCollapsed(): Promise<void> {
    try {
      const item = await this.storage.retrieve<boolean>(
        SIDEBAR_STORAGE_KEY,
        false,
      );
      this.cachedCollapsed = item?.value ?? false;
    } catch {
      this.cachedCollapsed = false;
    }
  }

  async getCollapsed(): Promise<boolean> {
    if (this.cachedCollapsed !== null) {
      return this.cachedCollapsed;
    }
    try {
      const item = await this.storage.retrieve<boolean>(
        SIDEBAR_STORAGE_KEY,
        false,
      );
      const value = item?.value ?? false;
      this.cachedCollapsed = value;
      return value;
    } catch {
      return false;
    }
  }

  async setCollapsed(value: boolean): Promise<void> {
    this.cachedCollapsed = value;
    await this.storage.store({ key: SIDEBAR_STORAGE_KEY, value });
  }
}
